/**
 * REQUIREMENT: REQ-016 - UI Accessibility
 * 
 * STATEMENT: "When users navigate the UI with keyboard,
 *            the system shall provide visible focus indicators,
 *            trap focus in modals, and announce errors via aria-live regions
 *            WITH WCAG 2.1 AA compliance
 *            WITHIN immediate response time for all interactions."
 * 
 * SUB-REQUIREMENTS:
 * - REQ-016.1: Keyboard Navigation
 *   • All interactive elements keyboard accessible
 *   • Tab order logical and predictable
 *   • No keyboard traps except modals
 *   • Shortcuts for common actions
 *   • Skip navigation links
 * 
 * - REQ-016.2: Focus Management
 *   • Visible focus indicators on all elements
 *   • Focus outline minimum 2px solid
 *   • Focus contrast ratio ≥ 3:1
 *   • Focus restored after modal close
 *   • Focus trap in modal dialogs
 * 
 * - REQ-016.3: Screen Reader Support
 *   • Proper ARIA labels and descriptions
 *   • Live regions for dynamic content
 *   • Error announcements with role="alert"
 *   • Loading state announcements
 *   • Form field associations
 * 
 * - REQ-016.4: Error Handling
 *   • Errors announced immediately
 *   • Error messages associated with fields
 *   • Clear error recovery instructions
 *   • Success confirmations announced
 *   • Validation feedback in real-time
 * 
 * - REQ-016.5: Modal Accessibility
 *   • Focus trapped within modal
 *   • Escape key closes modal
 *   • Focus returns to trigger element
 *   • Background content inert
 *   • Proper ARIA modal attributes
 * 
 * - REQ-016.6: Color and Contrast
 *   • Text contrast ratio ≥ 4.5:1 (normal)
 *   • Text contrast ratio ≥ 3:1 (large)
 *   • UI component contrast ≥ 3:1
 *   • Focus indicator contrast ≥ 3:1
 *   • Error state not only color-based
 * 
 * VERIFICATION:
 * - Automated testing with axe-core
 * - Keyboard navigation testing
 * - Screen reader testing (NVDA/JAWS)
 * - WCAG 2.1 AA validation
 * - Manual accessibility audit
 * - User testing with assistive technology
 * 
 * TRACES FROM:
 * - Stakeholder Need: SN-008 (Accessibility)
 * - Parent Requirement: FRN-001 (UI A11y)
 * - Business Rule: BR-009 (Legal Compliance)
 * - Use Case: UC-011 (Dashboard Navigation)
 * 
 * TRACES TO:
 * - Function: FN-UI-A11Y
 * - Component: web-frontend
 * - Standards: WCAG 2.1 AA
 * - Test: tests/e2e/accessibility.spec.ts
 * - Test: UI-A11y.feature
 * 
 * IMPLEMENTATION NOTES:
 * - Uses focus-visible for keyboard-only focus
 * - Implements custom focus trap hook
 * - Provides live region context for announcements
 * - Escape key handler for all modals
 * - Skip navigation as first focusable element
 * 
 * Accessibility Provider and Components
 * Provides comprehensive accessibility features
 * Requirements: FRN-001 (UI A11y Focus/Keyboard/Errors)
 */

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';

// ============================================================================
// Skip Navigation Component
// ============================================================================

export const SkipNavigation: React.FC = () => {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 
               bg-blue-600 text-white px-4 py-2 rounded-lg z-50 focus:outline-none 
               focus:ring-2 focus:ring-blue-500"
    >
      Skip to main content
    </a>
  );
};

// ============================================================================
// Focus Management Hook
// ============================================================================

export const useFocusTrap = (isActive: boolean) => {
  const containerRef = useRef<HTMLElement>(null);
  
  useEffect(() => {
    if (!isActive || !containerRef.current) return;
    
    const container = containerRef.current;
    const focusableElements = container.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), ' +
      'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;
    
    // Focus first element
    firstElement?.focus();
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };
    
    container.addEventListener('keydown', handleKeyDown);
    
    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive]);
  
  return containerRef;
};

// ============================================================================
// Live Region for Announcements
// ============================================================================

interface LiveRegionContextType {
  announce: (message: string, priority?: 'polite' | 'assertive') => void;
  announceError: (message: string) => void;
  announceSuccess: (message: string) => void;
}

const LiveRegionContext = createContext<LiveRegionContextType | undefined>(undefined);

export const useLiveRegion = () => {
  const context = useContext(LiveRegionContext);
  if (!context) {
    throw new Error('useLiveRegion must be used within LiveRegionProvider');
  }
  return context;
};

export const LiveRegionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [politeMessage, setPoliteMessage] = useState('');
  const [assertiveMessage, setAssertiveMessage] = useState('');
  const politeTimeoutRef = useRef<NodeJS.Timeout>();
  const assertiveTimeoutRef = useRef<NodeJS.Timeout>();
  
  const announce = (message: string, priority: 'polite' | 'assertive' = 'polite') => {
    if (priority === 'assertive') {
      // Clear previous timeout
      if (assertiveTimeoutRef.current) {
        clearTimeout(assertiveTimeoutRef.current);
      }
      
      setAssertiveMessage(message);
      
      // Clear message after 5 seconds
      assertiveTimeoutRef.current = setTimeout(() => {
        setAssertiveMessage('');
      }, 5000);
    } else {
      // Clear previous timeout
      if (politeTimeoutRef.current) {
        clearTimeout(politeTimeoutRef.current);
      }
      
      setPoliteMessage(message);
      
      // Clear message after 5 seconds
      politeTimeoutRef.current = setTimeout(() => {
        setPoliteMessage('');
      }, 5000);
    }
  };
  
  const announceError = (message: string) => {
    announce(`Error: ${message}`, 'assertive');
  };
  
  const announceSuccess = (message: string) => {
    announce(`Success: ${message}`, 'polite');
  };
  
  return (
    <LiveRegionContext.Provider value={{ announce, announceError, announceSuccess }}>
      {children}
      
      {/* Polite announcements */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {politeMessage}
      </div>
      
      {/* Assertive announcements (errors) */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      >
        {assertiveMessage}
      </div>
    </LiveRegionContext.Provider>
  );
};

// ============================================================================
// Focus Visible Enhancement
// ============================================================================

export const FocusVisibleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    // Add focus-visible polyfill styles
    const style = document.createElement('style');
    style.innerHTML = `
      /* Remove default focus outline */
      *:focus {
        outline: none;
      }
      
      /* Add visible focus for keyboard navigation */
      *:focus-visible {
        outline: 2px solid #2563eb !important;
        outline-offset: 2px !important;
      }
      
      /* Enhanced focus for buttons */
      button:focus-visible {
        outline: 3px solid #2563eb !important;
        outline-offset: 2px !important;
        box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1) !important;
      }
      
      /* Enhanced focus for inputs */
      input:focus-visible,
      textarea:focus-visible,
      select:focus-visible {
        outline: 2px solid #2563eb !important;
        outline-offset: 0 !important;
        border-color: #2563eb !important;
        box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1) !important;
      }
      
      /* Enhanced focus for links */
      a:focus-visible {
        outline: 2px solid #2563eb !important;
        outline-offset: 2px !important;
        border-radius: 2px;
      }
      
      /* Modal backdrop should not be focusable */
      [data-modal-backdrop] {
        outline: none !important;
      }
    `;
    
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  return <>{children}</>;
};

// ============================================================================
// Main Accessibility Provider
// ============================================================================

export const AccessibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <FocusVisibleProvider>
      <LiveRegionProvider>
        <SkipNavigation />
        <main id="main-content" tabIndex={-1}>
          {children}
        </main>
      </LiveRegionProvider>
    </FocusVisibleProvider>
  );
};